import "./EventBanner.scss"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { Modal, Icon } from "@fider/components"
import { actions } from "@fider/services"
import IconSpeakerphone from "@fider/assets/images/heroicons-speakerphone.svg"
import IconX from "@fider/assets/images/heroicons-x.svg"
import IconChevronUp from "@fider/assets/images/chevron-up.svg"
import { i18n } from "@lingui/core"

// Tùy biến KHCN: nút "Thông tin sự kiện" trên header mở một banner dạng slider.
// Ảnh sự kiện để trong static/events/1.(png|jpg|jpeg|webp), 2.*, ... phục vụ ở /static/assets/events/<n>.*
// Slider chỉ cuộn khi có nhiều hơn một ảnh; một ảnh thì hiện tĩnh. Nút chỉ xuất hiện khi có ít nhất một ảnh.

const AUTOPLAY_MS = 5000

export const EventBanner = () => {
  const [images, setImages] = useState<string[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const autoplay = useRef<number>()

  useEffect(() => {
    let cancelled = false
    actions.getEventBanners().then((r) => {
      if (!cancelled && r.ok) {
        setImages(r.data)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const count = images.length
  const go = useCallback((next: number) => setIndex(count === 0 ? 0 : ((next % count) + count) % count), [count])

  // Tự chuyển ảnh khi mở và có nhiều hơn một ảnh
  useEffect(() => {
    window.clearInterval(autoplay.current)
    if (isOpen && count > 1) {
      autoplay.current = window.setInterval(() => go(indexRef.current + 1), AUTOPLAY_MS)
    }
    return () => window.clearInterval(autoplay.current)
  }, [isOpen, count, go])

  // Giữ index mới nhất cho interval
  const indexRef = useRef(index)
  indexRef.current = index

  // Vuốt trên thiết bị cảm ứng
  const touchX = useRef<number | null>(null)
  const onTouchStart = (e: React.TouchEvent) => (touchX.current = e.touches[0].clientX)
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) {
      return
    }
    const dx = e.changedTouches[0].clientX - touchX.current
    if (Math.abs(dx) > 40) {
      go(index + (dx < 0 ? 1 : -1))
    }
    touchX.current = null
  }

  if (count === 0) {
    return null
  }

  const open = () => {
    setIndex(0)
    setIsOpen(true)
  }

  return (
    <>
      <button
        type="button"
        className="c-eventbtn"
        onClick={open}
        aria-label={i18n._({ id: "khcn.event.title", message: "Event information" })}
        title={i18n._({ id: "khcn.event.title", message: "Event information" })}
      >
        <Icon sprite={IconSpeakerphone} />
        <span className="c-eventbtn__dot" aria-hidden="true" />
      </button>

      <Modal.Window isOpen={isOpen} onClose={() => setIsOpen(false)} size="fluid" className="c-event-modal">
        <Modal.Content>
          <div className="c-event-modal__body">
            <button
              type="button"
              className="c-event-modal__close"
              onClick={() => setIsOpen(false)}
              aria-label={i18n._({ id: "khcn.welcome.close", message: "Close" })}
            >
              <Icon sprite={IconX} />
            </button>

            <div className="c-event-modal__viewport" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <div className="c-event-modal__track" style={{ transform: `translateX(-${index * 100}%)` }}>
                {images.map((src, i) => (
                  <div className="c-event-modal__slide" key={src} aria-hidden={i !== index}>
                    <img src={src} alt={i18n._({ id: "khcn.event.slide", message: "Event {n}", values: { n: i + 1 } })} />
                  </div>
                ))}
              </div>

              {count > 1 && (
                <>
                  <button type="button" className="c-event-modal__nav c-event-modal__nav--prev" onClick={() => go(index - 1)} aria-label="Prev">
                    <Icon sprite={IconChevronUp} />
                  </button>
                  <button type="button" className="c-event-modal__nav c-event-modal__nav--next" onClick={() => go(index + 1)} aria-label="Next">
                    <Icon sprite={IconChevronUp} />
                  </button>
                </>
              )}
            </div>

            {count > 1 && (
              <div className="c-event-modal__dots" role="tablist">
                {images.map((src, i) => (
                  <button
                    type="button"
                    key={src}
                    className={`c-event-modal__dot ${i === index ? "c-event-modal__dot--active" : ""}`}
                    aria-selected={i === index}
                    aria-label={`${i + 1}`}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </Modal.Content>
      </Modal.Window>
    </>
  )
}
